import type {PayloadAction} from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import type {
  InstanceSimpleResponse,
  UserInstanceModel,
  UserSessionResponse,
  ValidateSessionResponse,
} from 'src/api/ProactApiClient';
import {userApiClient} from 'src/api/ProactApiClientInstances';
import {emptySplitApi} from 'src/redux/api';

import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {purgeInstanceScopedState} from './instanceSwitchActions';

export {purgeInstanceScopedState} from './instanceSwitchActions';

export interface SessionState {
  isSessionInitialized: boolean;
  isTenantInitialized: boolean;
  session: ValidateSessionResponse | null;
  user: UserSessionResponse | null;
  instance: InstanceSimpleResponse | null;
  avatarUrl: string | null;
  instances: UserInstanceModel[];
  isSuperAdmin: boolean;
  isError: boolean;
  isSuccess: boolean;
  isSessionLoading: boolean;
  isSessionSkipLoading: boolean;
  isInstanceLoading: boolean;
  isInstanceListLoading: boolean;
  isAvatarLoading: boolean;
  message: string;
}

// Async thunk for Getting User Session Data
export const validateUserSession = createAsyncThunk(
  'auth/validateUserSession',
  async (_, thunkAPI) => {
    try {
      const response = await userApiClient.validateUserSession();
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(parseErrorMessage(error));
    }
  }
);
export const validateUserSessionSeamless = createAsyncThunk(
  'auth/validateUserSessionSeamless',
  async (_, thunkAPI) => {
    try {
      const response = await userApiClient.validateUserSession();
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(parseErrorMessage(error));
    }
  }
);

// Async thunk for Getting User Instances
export const getUserInstances = createAsyncThunk(
  'auth/getUserInstances',
  async (_, thunkAPI) => {
    try {
      const response = await userApiClient.getUserInstances();
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(parseErrorMessage(error));
    }
  }
);

// Async thunk for Setting User Active Instance
// Sequence: purge instance-scoped persisted slices BEFORE resetApiState, then the
// fulfilled reducer revalidates session state. Callers should dispatch
// validateUserSessionSeamless after this resolves to pick up the new user.id.
export const setActiveInstance = createAsyncThunk(
  'auth/setActiveInstance',
  async (instanceId: number, thunkAPI) => {
    try {
      const response = await userApiClient.setActiveInstance(instanceId);
      thunkAPI.dispatch(purgeInstanceScopedState());
      thunkAPI.dispatch(emptySplitApi.util.resetApiState());
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(parseErrorMessage(error));
    }
  }
);

export const getAvatarUrl = createAsyncThunk(
  'auth/getAvatarUrl',
  async (_, thunkAPI) => {
    try {
      const response = await userApiClient.getAvatarUrl();
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(parseErrorMessage(error));
    }
  }
);

// Initial state
const initialState: SessionState = {
  isSessionInitialized: false,
  isTenantInitialized: false,
  session: null,
  user: null,
  instance: null,
  avatarUrl: null,
  instances: [],
  isSuperAdmin: false,
  isError: false,
  isSuccess: false,
  isSessionLoading: false,
  isSessionSkipLoading: false,
  isInstanceLoading: false,
  isInstanceListLoading: false,
  isAvatarLoading: false,
  message: '',
};

// Slice
export const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    resetSessionState: () => initialState,
    resetForInstanceSwitch: () => initialState,
    setIsSessionInitiailized: (state, action) => {
      state.isSessionInitialized = action.payload;
    },
    setIsTenantInitiailized: (state, action) => {
      state.isTenantInitialized = action.payload;
    },
    setSkipSessionLoader: (state, action) => {
      state.isSessionSkipLoading = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(purgeInstanceScopedState, () => initialState)
      .addCase(validateUserSession.pending, state => {
        state.isSessionLoading = true;
        state.isError = false;
        state.isSuccess = false;
      })
      .addCase(
        validateUserSession.fulfilled,
        (state, action: PayloadAction<ValidateSessionResponse>) => {
          state.isSessionLoading = false;
          state.isSuccess = true;
          state.session = action.payload;
          state.user =
            action.payload !== null && action.payload !== undefined
              ? action.payload.user ?? null
              : null;
          state.isSuperAdmin = action.payload?.user?.isSuperAdmin ?? false;
          state.instance = action?.payload?.instance ?? null;
          state.avatarUrl = action?.payload?.user?.avatarUrl ?? null;
        }
      )
      .addCase(validateUserSession.rejected, (state, action) => {
        state.isSessionLoading = false;
        state.isError = true;
        state.session = null;
        state.user = null;
        state.isSuperAdmin = false;
        state.message = action.payload as string;
      })
      .addCase(validateUserSessionSeamless.pending, state => {
        state.isError = false;
        state.isSuccess = false;
      })
      .addCase(
        validateUserSessionSeamless.fulfilled,
        (state, action: PayloadAction<ValidateSessionResponse>) => {
          state.isSuccess = true;
          state.session = action.payload;
          state.user = action?.payload?.user ?? null;
          state.isSuperAdmin = action.payload?.user?.isSuperAdmin ?? false;
          state.instance = action?.payload?.instance ?? null;
          state.avatarUrl = action?.payload?.user?.avatarUrl ?? null;
        }
      )
      .addCase(validateUserSessionSeamless.rejected, (state, action) => {
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(setActiveInstance.pending, state => {
        state.isInstanceLoading = true;
        state.isError = false;
        state.isSuccess = false;
      })
      .addCase(setActiveInstance.fulfilled, state => {
        // Endpoint returns the new Instance only; the follow-up
        // validateUserSessionSeamless dispatch is the session source of truth.
        state.isInstanceLoading = false;
        state.isSuccess = true;
      })
      .addCase(setActiveInstance.rejected, (state, action) => {
        state.isInstanceLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(getUserInstances.pending, state => {
        state.isInstanceListLoading = true;
        state.isError = false;
        state.isSuccess = false;
      })
      .addCase(
        getUserInstances.fulfilled,
        (state, action: PayloadAction<UserInstanceModel[]>) => {
          state.isInstanceListLoading = false;
          state.isSuccess = true;
          state.instances = action.payload;
        }
      )
      .addCase(getUserInstances.rejected, (state, action) => {
        state.isInstanceListLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(getAvatarUrl.pending, state => {
        state.isAvatarLoading = true;
        state.isError = false;
        state.isSuccess = false;
      })
      .addCase(
        getAvatarUrl.fulfilled,
        (state, action: PayloadAction<string | undefined>) => {
          state.isAvatarLoading = false;
          state.isSuccess = true;
          state.avatarUrl = action.payload ?? null;
        }
      )
      .addCase(getAvatarUrl.rejected, (state, action) => {
        state.isAvatarLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      });
  },
});

export const {
  resetSessionState,
  resetForInstanceSwitch,
  setIsSessionInitiailized,
  setIsTenantInitiailized,
  setSkipSessionLoader,
} = sessionSlice.actions;

export default sessionSlice.reducer;
